/**
 * Service d'export des données
 * CSV et Excel (.xlsx) pour les transactions
 */

import * as XLSX from 'xlsx';
import { getFilteredTransactions, TransactionWithDetails } from './TransactionService';
import { getComptes, Compte } from './AccountService';
import { getCategories, Categorie } from './CategoryService';
import { TransactionPeriodiqueWithDetails } from './PeriodicTransactionService';
import { getFrequencyLabel } from '../../config/constants';

/**
 * Interface pour les données d'export
 */
export interface ExportData {
  transactions: TransactionWithDetails[];
  comptes: Compte[];
  categories: Categorie[];
}

/**
 * Fonction pour récupérer toutes les données pour l'export
 */
export const getAllExportData = async (): Promise<{ data: ExportData | null; error: any }> => {
  try {
    // Récupérer toutes les transactions
    const transactionsResult = await getFilteredTransactions({});
    if (transactionsResult.error) {
      return { data: null, error: transactionsResult.error };
    }

    // Récupérer tous les comptes
    const comptesResult = await getComptes();
    if (comptesResult.error) {
      return { data: null, error: comptesResult.error };
    }

    // Récupérer toutes les catégories
    const categoriesResult = await getCategories();
    if (categoriesResult.error) {
      return { data: null, error: categoriesResult.error };
    }

    const exportData: ExportData = {
      transactions: transactionsResult.data || [],
      comptes: comptesResult.data || [],
      categories: categoriesResult.data || []
    };

    return { data: exportData, error: null };
  } catch (error) {
    console.error('Exception lors de la récupération des données d\'export:', error);
    return { data: null, error };
  }
};

/**
 * Fonction pour exporter les transactions au format CSV
 */
export const exportTransactionsToCSV = async (): Promise<{ data: string | null; error: any }> => {
  try {
    const { data: exportData, error } = await getAllExportData();
    if (error) {
      return { data: null, error };
    }

    if (!exportData) {
      return { data: null, error: 'Aucune donnée à exporter' };
    }

    // Créer le contenu CSV
    const csvContent = generateCSVContent(exportData);
    return { data: csvContent, error: null };
  } catch (error) {
    console.error('Exception lors de l\'export CSV:', error);
    return { data: null, error };
  }
};

/**
 * Génère le contenu CSV à partir d'une liste de transactions (filtres appliqués côté UI).
 */
export const buildTransactionsCSVString = (transactions: TransactionWithDetails[]): string => {
  const headers = [
    'Date',
    'Description',
    'Montant',
    'Type',
    'Catégorie',
    'Compte',
    'Compte Destination',
    'Commentaire',
  ];

  const rows = transactions.map((transaction) => [
    formatDate(transaction.date),
    transaction.description || '',
    transaction.montant.toString(),
    transaction.type || 'depense',
    transaction.categorie?.nom || '',
    transaction.compte?.nom || '',
    transaction.compte_destination?.nom || '',
    '',
  ]);

  const csvRows = [headers, ...rows];
  return csvRows
    .map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(','))
    .join('\n');
};

/**
 * Génère le contenu CSV à partir des données (export global)
 */
const generateCSVContent = (exportData: ExportData): string => {
  return buildTransactionsCSVString(exportData.transactions);
};

/**
 * Formate une date pour l'affichage
 */
const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  } catch (error) {
    return dateString;
  }
};

/**
 * Fonction pour télécharger le fichier CSV
 */
export const downloadCSV = (csvContent: string, filename = 'transactions.csv'): void => {
  try {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Créer un lien de téléchargement
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    
    // Ajouter au DOM et déclencher le téléchargement
    document.body.appendChild(link);
    link.click();
    
    // Nettoyer
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Erreur lors du téléchargement du CSV:', error);
  }
};

/** Nom de fichier par défaut : transactions_YYYY-MM-DD */
export const defaultTransactionsExportBasename = (): string => {
  return `transactions_${new Date().toISOString().slice(0, 10)}`;
};

/**
 * Télécharge les transactions affichées (liste déjà filtrée) en CSV (UTF-8 avec BOM pour Excel).
 */
export const downloadTransactionsCSV = (transactions: TransactionWithDetails[], filename?: string): void => {
  const base = filename ?? `${defaultTransactionsExportBasename()}.csv`;
  const csv = buildTransactionsCSVString(transactions);
  downloadCSV(`\uFEFF${csv}`, base);
};

const escapeCsvField = (field: string | number | boolean): string =>
  `"${String(field).replace(/"/g, '""')}"`;

/**
 * Génère le CSV des transactions périodiques.
 */
export const buildPeriodicTransactionsCSVString = (
  transactions: TransactionPeriodiqueWithDetails[],
): string => {
  const headers = [
    'Description',
    'Montant',
    'Type',
    'Fréquence',
    'Catégorie',
    'Compte',
    'Compte destination',
    'Actif',
    'Prochaine exécution',
    'Date début',
    'Date fin',
  ];

  const rows = transactions.map((t) => [
    t.description || '',
    t.montant.toString(),
    t.type || '',
    getFrequencyLabel(t.frequence) || t.frequence || '',
    t.categorie?.nom || '',
    t.compte?.nom || '',
    t.compte_destination?.nom || '',
    t.actif ? 'Oui' : 'Non',
    t.prochaine_execution ? formatDate(t.prochaine_execution) : '',
    t.date_debut ? formatDate(t.date_debut) : '',
    t.date_fin ? formatDate(t.date_fin) : '',
  ]);

  return [headers, ...rows]
    .map((row) => row.map(escapeCsvField).join(','))
    .join('\n');
};

export const defaultPeriodicTransactionsExportBasename = (): string => {
  return `periodiques_${new Date().toISOString().slice(0, 10)}`;
};

/**
 * Télécharge les transactions périodiques en CSV (UTF-8 avec BOM pour Excel).
 */
export const downloadPeriodicTransactionsCSV = (
  transactions: TransactionPeriodiqueWithDetails[],
  filename?: string,
): void => {
  const base = filename ?? `${defaultPeriodicTransactionsExportBasename()}.csv`;
  const csv = buildPeriodicTransactionsCSVString(transactions);
  downloadCSV(`\uFEFF${csv}`, base);
};

/**
 * Télécharge les transactions en classeur Excel (.xlsx).
 */
export const downloadTransactionsXLSX = (transactions: TransactionWithDetails[], filename?: string): void => {
  try {
    const rows = transactions.map((t) => ({
      Date: formatDate(t.date),
      Description: t.description || '',
      Montant: t.montant,
      Type: t.type || 'depense',
      Catégorie: t.categorie?.nom || '',
      Compte: t.compte?.nom || '',
      'Compte destination': t.compte_destination?.nom || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Transactions');
    const name = filename ?? `${defaultTransactionsExportBasename()}.xlsx`;
    XLSX.writeFile(wb, name);
  } catch (error) {
    console.error('Erreur export XLSX:', error);
  }
};

/**
 * Fonction pour exporter les données filtrées
 */
export const exportFilteredTransactionsToCSV = async (
  filters: {
    startDate?: string;
    endDate?: string;
    compte_id?: string;
    categorie_id?: string;
  }
): Promise<{ data: string | null; error: any }> => {
  try {
    // Récupérer les transactions filtrées
    const { data: transactions, error } = await getFilteredTransactions(filters);
    if (error) {
      return { data: null, error };
    }

    if (!transactions || transactions.length === 0) {
      return { data: null, error: 'Aucune transaction trouvée pour les filtres spécifiés' };
    }

    // Récupérer les comptes et catégories pour l'enrichissement
    const comptesResult = await getComptes();
    const categoriesResult = await getCategories();

    const exportData: ExportData = {
      transactions: transactions,
      comptes: comptesResult.data || [],
      categories: categoriesResult.data || []
    };

    // Générer le CSV
    const csvContent = generateCSVContent(exportData);
    return { data: csvContent, error: null };
  } catch (error) {
    console.error('Exception lors de l\'export filtré:', error);
    return { data: null, error };
  }
}; 